"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { INITIAL_REVEAL_COUNT, REVEAL_INCREMENT, SCROLL_RESTORE_KEY } from "../lifestyle-constants";

interface RestoredPosition {
  revealed: number;
  scrollY: number;
  /** Which result set the position belongs to; a filter change invalidates it. */
  key: string;
}

function readRestorePoint(): RestoredPosition | null {
  try {
    const raw = sessionStorage.getItem(SCROLL_RESTORE_KEY);
    return raw ? (JSON.parse(raw) as RestoredPosition) : null;
  } catch {
    return null;
  }
}

function isPageReload(): boolean {
  const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  return nav?.type === "reload";
}

/** Scrolls to the top when the viewport sits past the rendered page height. */
function scrollToStartIfPastContent(): void {
  const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  if (window.scrollY > maxScroll) {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }
}

/**
 * Reveals a long list in chunks as the user scrolls.
 *
 * Replaces `loadMore`, `isHidden` and `infiniteScrollEvent`. Position is kept
 * in sessionStorage rather than the `botb-lifestyle-scroll` cookie the Angular
 * version used, so it does not ride along on every request.
 *
 * @param totalCount Length of the list being revealed.
 * @param resetKey Changes whenever the result set changes, resetting the reveal.
 */
export default function useProgressiveReveal(totalCount: number, resetKey: string) {
  const [revealed, setRevealed] = useState(INITIAL_REVEAL_COUNT);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasRestored = useRef(false);
  const hasHandledInitialResetKey = useRef(false);

  const hasMore = revealed < totalCount;

  // Stop the browser from replaying a deep scroll on refresh before React mounts.
  useEffect(() => {
    const previous = history.scrollRestoration;
    history.scrollRestoration = "manual";
    return () => {
      history.scrollRestoration = previous;
    };
  }, []);

  const loadMore = useCallback(() => {
    setRevealed(current => Math.min(current + REVEAL_INCREMENT, Math.max(totalCount, INITIAL_REVEAL_COUNT)));
  }, [totalCount]);

  // A new result set starts from the top again.
  useEffect(() => {
    if (!hasHandledInitialResetKey.current) {
      hasHandledInitialResetKey.current = true;
      return;
    }

    setRevealed(INITIAL_REVEAL_COUNT);
  }, [resetKey]);

  // Restore where the user left off, but only for the same result set —
  // returning to a different filter state should not jump down the page.
  // A hard refresh always starts from the top: the first paint only reveals a
  // slice of the grid, so a restored deep scroll would land on empty space.
  useEffect(() => {
    if (hasRestored.current || totalCount === 0) return;
    hasRestored.current = true;

    if (isPageReload()) {
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior }));
      return;
    }

    const restorePoint = readRestorePoint();
    if (!restorePoint || restorePoint.key !== resetKey) {
      requestAnimationFrame(scrollToStartIfPastContent);
      return;
    }

    setRevealed(Math.min(Math.max(restorePoint.revealed, INITIAL_REVEAL_COUNT), totalCount));
    requestAnimationFrame(() => window.scrollTo({ top: restorePoint.scrollY, behavior: "instant" as ScrollBehavior }));
  }, [totalCount, resetKey]);

  // Progressive reveal grows the page after load; clamp any stale scroll offset.
  useEffect(() => {
    if (totalCount === 0) return;

    const frame = requestAnimationFrame(scrollToStartIfPastContent);
    return () => cancelAnimationFrame(frame);
  }, [totalCount, revealed, resetKey]);

  // Persist on the way out rather than on every scroll event.
  useEffect(() => {
    const persist = () => {
      try {
        sessionStorage.setItem(
          SCROLL_RESTORE_KEY,
          JSON.stringify({ revealed, scrollY: window.scrollY, key: resetKey } satisfies RestoredPosition)
        );
      } catch {
        // Private browsing or a full quota; losing the position is acceptable.
      }
    };

    window.addEventListener("pagehide", persist);
    return () => {
      window.removeEventListener("pagehide", persist);
      persist();
    };
  }, [revealed, resetKey]);

  useEffect(() => {
    if (!hasMore) return;

    let observer: IntersectionObserver | undefined;

    // Wait for the grid to commit — the sentinel remounts when sort or other
    // filters swap the lobby for the flat results view, and observing too early
    // leaves the observer attached to a detached node.
    const frame = requestAnimationFrame(() => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return;

      observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) loadMore();
        },
        // Start loading before the sentinel is actually on screen.
        { rootMargin: "400px" }
      );

      observer.observe(sentinel);
    });

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [hasMore, loadMore, resetKey, revealed]);

  return { revealed, hasMore, loadMore, sentinelRef };
}
